import { getFeasibilityStudyQueryOptions } from "@packages/api-client/hooks/feasibilityController";
import type {
	CowCalfResults,
	FeasibilityStudyDetail,
} from "@packages/shared-types";
import { FeasibilityStudyType } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCalculateStudy } from "@/api/feasibility/studies";
import { PageHeader } from "@/components/PageHeader";
import { CowCalfWizard } from "../-components/CowCalfWizard";
import { ResultsDashboard } from "../-components/ResultsDashboard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/feasibility/$studyId/",
)({
	component: FeasibilityStudyDetailPage,
});

function FeasibilityStudyDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { orgId, studyId } = useParams({
		from: "/_auth/app/_app/org/$orgId/feasibility/$studyId/",
	});
	const [showWizard, setShowWizard] = useState(false);

	const { data: study, isLoading } = useQuery(
		getFeasibilityStudyQueryOptions(orgId, studyId),
	);

	const calculateMutation = useCalculateStudy();

	const handleComplete = () => {
		calculateMutation.mutate({ organizationId: orgId, studyId });
	};

	const handleCancel = () => {
		navigate({ to: "/app/org/$orgId/feasibility", params: { orgId } });
	};

	const handleEditInputs = () => {
		setShowWizard(true);
	};

	const handleRecalculate = () => {
		calculateMutation.mutate(
			{ organizationId: orgId, studyId },
			{
				onSuccess: () => {
					setShowWizard(false);
				},
			},
		);
	};

	if (isLoading) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
			</div>
		);
	}

	if (!study) {
		return (
			<div className="h-full flex items-center justify-center">
				<p className="text-base-content/70">{t("common.notFound")}</p>
			</div>
		);
	}

	const isCalculated = study.status === "CALCULATED" && study.results;
	const showResults = isCalculated && !showWizard;

	return (
		<div className="h-full flex flex-col space-y-3">
			<PageHeader
				title={study.name}
				description={study.description ?? undefined}
				showBackButton
				backButtonText={t("common.back")}
			/>

			{showResults && study.type === FeasibilityStudyType.COW_CALF ? (
				<ResultsDashboard
					results={study.results as CowCalfResults}
					currency={study.currency}
					onEditInputs={handleEditInputs}
					onRecalculate={handleRecalculate}
				/>
			) : (
				study.type === FeasibilityStudyType.COW_CALF && (
					<CowCalfWizard
						study={study as unknown as FeasibilityStudyDetail}
						orgId={orgId}
						onComplete={handleComplete}
						onCancel={handleCancel}
						onRecalculate={handleRecalculate}
						isCalculated={!!isCalculated}
					/>
				)
			)}
		</div>
	);
}
